"use client";
import isBoolean from "lodash/isBoolean";
import { useEffect } from "react";

import { useConfig } from "@stadium/hooks/lib/use-config";

import Pill from "@stadium/ui/src/components/pill";
import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Label from "@stadium/ui/src/components/form/Label";

import { useCheckoutContext } from "@stadium/checkout/src/contexts/checkout";

import { ExternalLinkButton } from "@stadium/checkout/src/components/shared/buttons";
import Button from "@stadium/checkout/src/components/shared/button";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import {
  ANALYTICS_EVENT_ACTION_VIEWED,
  ANALYTICS_EVENT_ACTION_SELECTED,
  ANALYTICS_EVENT_ACTION_DESELECTED,
} from "@stadium/services/analytics/constants/default";
import { PROTECTION_TARGET } from "@stadium/checkout/src/constants/analytics";

import { POMP_TERM_OF_USE_URL } from "@stadium/constants/external-routes";
import { POMP_STEP_STATE } from "@stadium/checkout/src/constants/order";

import { getPompDescription, getPompLabel } from "@stadium/redux-utils/selectors/configSelector";

import styles from "./main.module.scss";

const PompAddOnSection = ({
  pompFee,
  pillCustomClass,
  renderContinueCta = () => {},
  handleEditPomp = () => {},
}) => {
  const { pompSelected, setPompSelected, currentCheckoutStep, checkoutStepsState, orderData } =
    useCheckoutContext();

  const pompLabel = useConfig(getPompLabel);
  const pompDescription = useConfig(getPompDescription);

  const withAnalytics = useWithAnalytics();

  const isPompStepSubmitted = checkoutStepsState[POMP_STEP_STATE]?.submitted;
  const isCurrentCheckoutStatePomp =
    currentCheckoutStep === checkoutStepsState[POMP_STEP_STATE]?.key;

  useEffect(() => {
    if (isCurrentCheckoutStatePomp) {
      withAnalytics(undefined, {
        track: true,
        target: PROTECTION_TARGET,
        action: ANALYTICS_EVENT_ACTION_VIEWED,
      })();
    }
  }, [isCurrentCheckoutStatePomp]);

  const renderPompSectionHeader = () => (
    <div className={styles.textInfoBox}>
      <div className={styles.headingContent}>
        <i className={styles.paymentIcon} />
        <span>{pompLabel}</span>
        {isPompStepSubmitted && (
          <Button
            styleType="link"
            onClick={() => handleEditPomp(POMP_STEP_STATE)}
            className={styles.editLink}
          >
            Edit
          </Button>
        )}
      </div>
    </div>
  );

  const renderTaxTerms = () => (
    <div className={styles.termTax}>
      *Our Peace of Mind Protection is not insurance. {""}
      <ExternalLinkButton
        className={styles.termLink}
        href={POMP_TERM_OF_USE_URL}
        styleType="link"
        btnStyle="no-style"
      >
        Terms
      </ExternalLinkButton>
    </div>
  );

  const renderProtectOrderLabel = () => (
    <div className={styles.labelData}>
      <span>Yes, protect my order</span>
      <Pill color="light" className={pillCustomClass}>
        {pompFee}
      </Pill>
    </div>
  );

  const renderDoNotProtectOrderLabel = () => (
    <div className={styles.labelData}>
      <span>No, do not protect my order</span>
    </div>
  );

  const renderPompReview = () => (
    <article className={styles.pompProtectBox}>
      {renderPompSectionHeader()}
      <div>{pompSelected ? renderProtectOrderLabel() : renderDoNotProtectOrderLabel()}</div>
      {renderTaxTerms()}
    </article>
  );

  const renderPompOptions = () => (
    <article className={styles.pompProtectBox}>
      {renderPompSectionHeader()}

      <div className={styles.boxHeading}>{pompDescription}</div>

      <div>
        <RadioInput
          id={"one"}
          checked={pompSelected}
          name={"card"}
          onClick={() => {
            withAnalytics(undefined, {
              track: true,
              target: PROTECTION_TARGET,
              action: ANALYTICS_EVENT_ACTION_SELECTED,
            })();
            setPompSelected(true);
          }}
          label={
            <Label iconType="radioOutline" styles={styles} className={styles.inputLabel}>
              {renderProtectOrderLabel()}
            </Label>
          }
        />
        <div className={styles.divider} />
        <RadioInput
          id={"two"}
          name={"card"}
          checked={pompSelected === false}
          onClick={() => {
            withAnalytics(setPompSelected, {
              track: true,
              target: PROTECTION_TARGET,
              action: ANALYTICS_EVENT_ACTION_DESELECTED,
            })(false);
          }}
          label={
            <Label iconType="radioOutline" styles={styles} className={styles.inputLabel}>
              {renderDoNotProtectOrderLabel()}
            </Label>
          }
        />
      </div>

      {renderTaxTerms()}
      <div className={styles.continueActionBlock}>
        {renderContinueCta({
          isDisabled: !isBoolean(pompSelected),
          payload: orderData.hasPomp === pompSelected ? {} : { hasPomp: pompSelected },
        })}
      </div>
    </article>
  );

  if (isCurrentCheckoutStatePomp) return renderPompOptions();

  if (isPompStepSubmitted) return renderPompReview();

  return renderPompSectionHeader();
};

export default PompAddOnSection;
